import React, { memo, useRef, useContext } from 'react'
import PropTypes from 'prop-types'

import { util } from '../../../util'
import { Spinner, Icon } from '../index'
import { LanguageContext } from '../../../context/LanguageContext'
import { formatString } from '../../../context/LanguageContext/strings'

/** This component will return either an image requirements, or an uploaded image along with its details */
const UploadImage = ({
    imgURL,
    isBase64,
    className,
    onChange,
    onLoad,
    rules,
    maxHeight,
    maxWidth,
    clearImage,
    loading,
    lastModifiedAt,
    size,
    // type
}) => {
    //Localization
    const { strings } = useContext(LanguageContext)

    const editRef = useRef(null)

    const renderImageReq = () => (
        <div className={'upload-image__container'}>
            <div className={'choose-file'}>
                <div>
                    <Icon pointer={true} size={'lg'} renderIcon={util.CloudUploadOutlinedIcon} />
                </div>
                <div>
                    <p className={'upload-image__text'}>{strings.UPLOAD_IMAGE_DRAG} </p>
                    <label className={'upload-image__choose-file'} htmlFor="files">
                        {strings.UPLOAD_IMAGE_CHOOSE}
                    </label>
                    <input
                        ref={editRef}
                        id="files"
                        className={'upload-image__input-file'}
                        type="file"
                        onChange={onChange}
                    />
                </div>
            </div>
            <div className={'requirements'}>
                <p className={'paragraph italic bold'}>{strings.UPLOAD_IMAGE_REQUIREMENTS}</p>
                <p className={'paragraph'}>
                    {formatString('UPLOAD_IMAGE_DIMENSIONS', {
                        SIZEW: rules.maxWidth,
                        SIZEH: rules.maxHeight,
                    })}
                </p>
                <p className={'paragraph'}>
                    {formatString('UPLOAD_IMAGE_FORMAT', {
                        FORMAT: rules.imgFormat.map(el => `${el}, `),
                    })}
                </p>
                <p className={'paragraph'}>
                    {formatString('UPLOAD_IMAGE_SIZE', { SIZE: rules.maxImgSize / 1000 })}
                </p>
            </div>
        </div>
    )

    const renderUploadedImage = () => (
        <div className={'upload-image__preview'}>
            <img
                src={imgURL}
                className={maxHeight && maxWidth && 'preview-image'}
                onLoad={onLoad}
                alt={strings.previewAltTag}
            />
            <div className={'upload-image--aside'}>
                <div className={'uploaded-img-config'}>
                    <div className={'requirements'}>
                        <p className={'paragraph italic bold'}>
                            {isBase64
                                ? strings.UPLOAD_IMAGE_UPLOADED
                                : imgURL.split(
                                      /\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+([+-][0-2]\d:[0-5]\d|Z)/
                                  )[2]}
                        </p>
                        <p className={'paragraph'}>
                            {lastModifiedAt && util.getFormattedDate(lastModifiedAt, 'MMM D, yyyy')}
                        </p>
                        <p className={'paragraph'}>
                            {formatString('UPLOAD_IMAGE_UPLOADED_SIZE', { SIZE: size / 1000 })}
                        </p>
                        <p className={'paragraph'}>
                            {formatString('UPLOAD_IMAGE_UPLOADED_PX', {
                                WIDTH: maxWidth,
                                HEIGHT: maxHeight,
                            })}
                        </p>
                    </div>
                </div>
                <div className={'uploaded-actions'}>
                    <label className={'paragraph sub px-4'} htmlFor="files">
                        {strings.EDIT}
                    </label>
                    <input
                        ref={editRef}
                        id="files"
                        className={'input-file'}
                        type="file"
                        onChange={onChange}
                    />
                    <p className={'paragraph sub'} onClick={() => clearImage(imgURL)}>
                        {strings.DELETE}
                    </p>
                </div>
            </div>
        </div>
    )

    if (loading) {
        return <Spinner isInline={true} />
    }

    return (
        <div className={className ? className : 'upload-image'}>
            {!imgURL && renderImageReq()}
            {imgURL && !loading && renderUploadedImage()}
        </div>
    )
}

UploadImage.propTypes = {
    /** @param imgURL {String} -> CloudFront promotion banner url */
    imgURL: PropTypes.string,

    /** @param onChange {EventHandler} -> change handler to handle image uploads */
    onChange: PropTypes.func,

    /** @param onLoad {EventHandler} -> load handler to handle after the image has been uploaded */
    onLoad: PropTypes.func,

    /** @param rules {Object} -> object consists of image uploading rules */
    rules: PropTypes.object,

    /** @param maxHeight {Number} maximum height of the image */
    maxHeight: PropTypes.number,

    /** @param maxWidth {Number} maximum width of the image */
    maxWidth: PropTypes.number,

    /** @param clearImage {EventHandler} -> Click Handler to remove image */
    clearImage: PropTypes.func,

    /** @param loading {Boolean} -> state when uploading image */
    loading: PropTypes.bool,

    /** @param lastModifiedAt {Date} -> At what date an image has been uploaded */
    lastModifiedAt: PropTypes.string,

    /** @param size {String} -> image size value */
    size: PropTypes.number,
}

export default memo(UploadImage)
